import { useEffect, useRef, useState } from "react";

import { eventEmitter } from "../eventEmitters/EventEmitters";
import { shareApi } from "./shareStore";
import { copyShareLink } from "./ShareLinksList";
import { DEFAULT_EXPIRY_DAYS, shareKey, shareLink, ShareSnapshot, shareState } from "./shareSnapshot";
import "./share.scss";

const day = (ms: number) => new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const madeAt = (s: ShareSnapshot) => new Date(s.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const startOf = (s: ShareSnapshot) => new Date(s.startMs).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const length = (s: ShareSnapshot) => {
  const min = Math.max(1, Math.round((s.endMs - s.startMs) / 60000));

  return min >= 60 ? `${Math.floor(min / 60)} h${min % 60 ? ` ${min % 60} min` : ""}` : `${min} min`;
};
const STATUS: Record<string, string> = { live: "Live", expired: "Expired", revoked: "Turned off" };

/** the row's own menu (a row's actions live in its three dots, like every table in the app): the app's dropdown look, closed by a click elsewhere or Escape */
const RowMenu = ({ s, state }: { s: ShareSnapshot; state: string }) => {
  const [open, setOpen] = useState(false);
  // where the list is drawn: FIXED to the page, from the button's own corner. The table sits in a box that scrolls sideways (overflow-x), which also clips downwards, so a list positioned inside it was cut off to a sliver under a one-row table
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const toggle = () => {
    if (open) { setOpen(false); return; }
    const r = box.current?.getBoundingClientRect();
    if (r) setAt({ top: r.bottom + 4, right: window.innerWidth - r.right });
    setOpen(true);
  };
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    const onDown = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);   // a scroll anywhere (the page or the table) moves the button away from the fixed list

    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("mousedown", onDown); window.removeEventListener("resize", close); window.removeEventListener("scroll", close, true); };
  }, [open]);
  if (state === "revoked") return null;
  const run = (fn: () => unknown) => () => { setOpen(false); void fn(); };

  return (
    <div className="share-row-menu" ref={box}>
      <button type="button" className="share-row-menu-btn" data-share-row-menu aria-label="Clip actions" aria-haspopup="menu" aria-expanded={open} onClick={toggle}>
        <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true"><circle cx="10" cy="4" r="1.7" fill="currentColor" /><circle cx="10" cy="10" r="1.7" fill="currentColor" /><circle cx="10" cy="16" r="1.7" fill="currentColor" /></svg>
      </button>
      {open && (
        <ul className="share-row-menu-list" role="menu" data-share-row-menu-list style={at ? { position: "fixed", top: at.top, right: at.right, left: "auto" } : undefined}>
          {s.token && <li role="none"><button type="button" role="menuitem" data-share-copy-item onClick={run(() => copyShareLink(s.token))}>Copy link</button></li>}
          {s.token && <li role="none"><button type="button" role="menuitem" data-share-open-item onClick={run(() => window.open(shareLink(window.location.origin, s.token), "_blank", "noopener"))}>Open</button></li>}
          <li role="none"><button type="button" role="menuitem" data-share-renew onClick={run(() => shareApi.renew(shareKey(s), DEFAULT_EXPIRY_DAYS))}>Renew</button></li>
          <li role="none"><button type="button" role="menuitem" data-share-revoke onClick={run(() => shareApi.revoke(shareKey(s)))}>Turn off</button></li>
        </ul>
      )}
    </div>
  );
};

/**
 * The replay clip links as a table, in the look of the Settings tables (heading, grey uppercase column labels, rows with a thin rule, a status dot, the row's actions in its three dots). It loads for itself and reloads
 * whenever a link is made, renewed or turned off anywhere ("sharesChanged").
 */
const ShareLinksTable = () => {
  const [shares, setShares] = useState<ShareSnapshot[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const refresh = () => shareApi.list().then((list) => { setShares(list); setFailed(false); setLoaded(true); }).catch(() => { setFailed(true); setLoaded(true); });
  useEffect(() => {
    refresh();
    eventEmitter.addListener("sharesChanged", refresh);

    return () => { eventEmitter.removeListener("sharesChanged", refresh); };
  }, []);
  const showViews = shares.some((s) => typeof s.views === "number");   // only a server store counts views
  if (!loaded) return null;
  if (failed) return <p className="share-table-empty" data-share-list-failed>Your replay clips could not be loaded. <button type="button" className="transparent-btn" onClick={refresh}>TRY AGAIN</button></p>;
  if (shares.length === 0) return <p className="share-table-empty" data-share-list-empty>No replay clips shared yet.</p>;

  return (
    <div className="share-table-wrap">
      <table className="share-table" data-share-list>
        <thead>
          <tr><th>Clip</th><th>Made</th><th>Length</th><th>Link expires</th>{showViews && <th>Views</th>}<th>Status</th><th aria-label="Actions" /></tr>
        </thead>
        <tbody>
          {shares.map((s) => {
            const state = shareState(s, Date.now());

            return (
              <tr key={shareKey(s)} data-share-item={shareKey(s)} data-share-state={state}>
                <td><span className="share-table-name">{s.label || "Untitled"}</span><span className="share-table-sub">{startOf(s)}</span></td>
                <td>{madeAt(s)}</td>
                <td>{length(s)}</td>
                <td>{state === "revoked" ? "Not available" : day(s.expiresAt)}</td>
                {showViews && <td>{s.views || 0}</td>}
                <td><span className={`share-status share-status-${state}`}><i aria-hidden="true" />{STATUS[state]}</span></td>
                <td className="share-table-actions"><RowMenu s={s} state={state} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ShareLinksTable;
